import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  User, Plus, Trash2, Save, Eye, Download, Copy, Check, 
  Image, Phone, Mail, MapPin, Users, Heart, QrCode, X,
  Upload, Camera, ExternalLink, Palette, Edit3, FileText,
  Star, Share2
} from 'lucide-react';
import { 
  FaYoutube, FaWhatsapp, FaFacebook, FaInstagram, FaTelegram,
  FaPhone, FaGlobe
} from 'react-icons/fa';
import { QRCodeSVG } from 'qrcode.react';
import html2canvas from 'html2canvas';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { getImageUrl } from '../../utils/imageUrl';
import { copyToClipboard } from '../../utils/clipboard';

// Social platforms configuration
const SOCIAL_PLATFORMS = [
  { id: 'youtube', name: 'YouTube', icon: FaYoutube, color: '#FF0000', bgColor: '#FF000020' },
  { id: 'whatsapp', name: 'WhatsApp', icon: FaWhatsapp, color: '#25D366', bgColor: '#25D36620' },
  { id: 'facebook', name: 'Facebook', icon: FaFacebook, color: '#1877F2', bgColor: '#1877F220' },
  { id: 'instagram', name: 'Instagram', icon: FaInstagram, color: '#E4405F', bgColor: '#E4405F20' },
  { id: 'telegram', name: 'Telegram', icon: FaTelegram, color: '#0088cc', bgColor: '#0088cc20' },
  { id: 'website', name: 'Website', icon: FaGlobe, color: '#6366F1', bgColor: '#6366F120' },
];

// Font options for biodata
const FONT_OPTIONS = [
  { id: 'default', name: 'Default (Poppins)', family: "'Poppins', sans-serif" },
  { id: 'serif', name: 'Traditional Serif', family: "'Georgia', 'Times New Roman', serif" },
  { id: 'devanagari', name: 'Hindi/Devanagari', family: "'Noto Sans Devanagari', 'Mangal', sans-serif" },
  { id: 'elegant', name: 'Elegant Script', family: "'Playfair Display', serif" },
  { id: 'modern', name: 'Modern Clean', family: "'Inter', 'Helvetica', sans-serif" },
  { id: 'formal', name: 'Formal Classic', family: "'Merriweather', serif" },
  { id: 'royal', name: 'Royal (Cinzel)', family: "'Cinzel', serif" },
];

// Default templates - Traditional, Modern, Premium designs
const DEFAULT_TEMPLATES = [
  // Traditional Templates
  {
    id: 'gold-royal',
    name: 'Gold Royal',
    category: 'traditional',
    colors: { primary: '#D4AF37', secondary: '#1a365d', background: '#FDF5E6', text: '#1a1a1a', accent: '#ffd700', border: '#D4AF37' }
  },
  {
    id: 'blue-gold',
    name: 'Blue Gold',
    category: 'traditional',
    colors: { primary: '#D4AF37', secondary: '#0f1419', background: '#1a365d', text: '#ffffff', accent: '#ffd700', border: '#D4AF37' }
  },
  {
    id: 'cream-classic',
    name: 'Cream Classic',
    category: 'traditional',
    colors: { primary: '#8B4513', secondary: '#2d1810', background: '#FFFDD0', text: '#1a1a1a', accent: '#D4AF37', border: '#8B4513' }
  },
  {
    id: 'maroon-gold',
    name: 'Maroon Gold',
    category: 'traditional',
    colors: { primary: '#800000', secondary: '#4a0404', background: '#FFF8E7', text: '#1a1a1a', accent: '#D4AF37', border: '#800000' }
  },
  {
    id: 'temple-orange',
    name: 'Temple Orange',
    category: 'traditional',
    colors: { primary: '#FF6B35', secondary: '#2d1810', background: '#FFF5E6', text: '#1a1a1a', accent: '#FFD700', border: '#FF6B35' }
  },
  // Modern Templates
  {
    id: 'colorful-modern',
    name: 'Colorful Modern',
    category: 'modern',
    colors: { primary: '#D4AF37', secondary: '#1a1a1a', background: '#FDF5E6', text: '#1a1a1a', accent: '#ff6b6b', border: '#D4AF37' }
  },
  {
    id: 'minimal-white',
    name: 'Minimal White',
    category: 'modern',
    colors: { primary: '#333333', secondary: '#666666', background: '#FFFFFF', text: '#1a1a1a', accent: '#D4AF37', border: '#E0E0E0' }
  },
  {
    id: 'dark-elegant',
    name: 'Dark Elegant',
    category: 'modern',
    colors: { primary: '#D4AF37', secondary: '#1a1a1a', background: '#1a1a2e', text: '#ffffff', accent: '#ffd700', border: '#D4AF37' }
  },
  {
    id: 'rose-blush',
    name: 'Rose Blush',
    category: 'modern',
    colors: { primary: '#C77DFF', secondary: '#9D4EDD', background: '#FFF0F5', text: '#1a1a1a', accent: '#FF69B4', border: '#C77DFF' }
  },
  // Premium Templates
  {
    id: 'royal-purple',
    name: 'Royal Purple',
    category: 'premium',
    colors: { primary: '#7B2CBF', secondary: '#3C096C', background: '#F8F0FF', text: '#1a1a1a', accent: '#D4AF37', border: '#7B2CBF' }
  },
  {
    id: 'emerald-luxury',
    name: 'Emerald Luxury',
    category: 'premium',
    colors: { primary: '#2E8B57', secondary: '#1B4D3E', background: '#F0FFF0', text: '#1a1a1a', accent: '#D4AF37', border: '#2E8B57' }
  },
  {
    id: 'sapphire-royal',
    name: 'Sapphire Royal',
    category: 'premium',
    colors: { primary: '#0F52BA', secondary: '#082567', background: '#F0F8FF', text: '#1a1a1a', accent: '#D4AF37', border: '#0F52BA' }
  }
];

// Horoscope/Rashi options
const RASHI_OPTIONS = [
  'Mesh (Aries)', 'Vrishabh (Taurus)', 'Mithun (Gemini)', 'Kark (Cancer)',
  'Simha (Leo)', 'Kanya (Virgo)', 'Tula (Libra)', 'Vrishchik (Scorpio)',
  'Dhanu (Sagittarius)', 'Makar (Capricorn)', 'Kumbh (Aquarius)', 'Meen (Pisces)'
];

const NAKSHATRA_OPTIONS = [
  'Ashwini', 'Bharani', 'Krittika', 'Rohini', 'Mrigashira', 'Ardra', 'Punarvasu',
  'Pushya', 'Ashlesha', 'Magha', 'Purva Phalguni', 'Uttara Phalguni', 'Hasta',
  'Chitra', 'Swati', 'Vishakha', 'Anuradha', 'Jyeshtha', 'Mula', 'Purva Ashadha',
  'Uttara Ashadha', 'Shravana', 'Dhanishta', 'Shatabhisha', 'Purva Bhadrapada',
  'Uttara Bhadrapada', 'Revati'
];

const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];

const COMPLEXION_OPTIONS = ['Very Fair', 'Fair', 'Wheatish', 'Dusky', 'Dark'];

const BODY_TYPE_OPTIONS = ['Slim', 'Athletic', 'Average', 'Heavy'];

const MARITAL_STATUS_OPTIONS = ['Never Married', 'Divorced', 'Widowed', 'Awaiting Divorce'];

const BioDataBuilder = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const bioCardRef = useRef(null);
  const fileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [templates, setTemplates] = useState(DEFAULT_TEMPLATES);
  const [myBiodatas, setMyBiodatas] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [activeTab, setActiveTab] = useState('create'); // create, list
  const [activeSection, setActiveSection] = useState('personal');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoUploadIndex, setPhotoUploadIndex] = useState(null);
  const [showQR, setShowQR] = useState(false);
  const [copied, setCopied] = useState(false);
  
  // Limits state
  const [limitsInfo, setLimitsInfo] = useState({
    tier: 'free',
    max_biodatas: 1,
    current_count: 0,
    remaining: 1,
    can_create: true
  });

  // Form data with enhanced fields
  const [formData, setFormData] = useState({
    template: 'gold-royal',
    font_family: 'default',
    religious_text: '!! SHRI GANESHAY NAMAH !!\n!! JAI JHULELA !!',
    title: 'BIODATA',
    personal_info: {
      name: '',
      date_of_birth: '',
      time_of_birth: '',
      place_of_birth: '',
      height: '',
      weight: '',
      blood_group: '',
      complexion: '',
      body_type: '',
      marital_status: 'Never Married',
      occupation: '',
      education: '',
      income: '',
      company: ''
    },
    horoscope_info: {
      rashi: '',
      nakshatra: '',
      gotra: '',
      manglik: '',
      gan: '',
      nadi: ''
    },
    family_info: {
      father_name: '',
      father_occupation: '',
      mother_name: '',
      mother_occupation: '',
      grandfather_name: '',
      native_place: '',
      brother: '',
      sister: '',
      family_type: '', // Joint, Nuclear
      family_status: '', // Middle Class, Upper Middle Class, Rich
      caste_community: ''
    },
    expectations: {
      age_range: '',
      height_range: '',
      education: '',
      occupation: '',
      location: '',
      other_preferences: ''
    },
    contact_info: {
      mobile_number: '',
      secondary_number: '',
      email: '',
      residential_address: '',
      city: '',
      state: '',
      pincode: ''
    },
    profile_photos: [],
    social_links: [],
    custom_fields: [],
    qr_enabled: true,
    qr_content: '',
    is_published: false
  });

  const [templateFilter, setTemplateFilter] = useState('all'); // all, traditional, modern, premium
  const [newSocialLink, setNewSocialLink] = useState({ platform: 'whatsapp', url: '', label: '' });
  const [newCustomField, setNewCustomField] = useState({ label: '', value: '' });

  // Filter templates by category
  const filteredTemplates = templateFilter === 'all' 
    ? templates 
    : templates.filter(t => t.category === templateFilter);

  useEffect(() => {
    fetchTemplates();
    fetchMyBiodatas();
    fetchLimits();
  }, []);

  const fetchTemplates = async () => {
    try {
      const res = await api.get('/biodata/templates');
      if (res.data?.templates?.length > 0) {
        setTemplates(res.data.templates);
      }
    } catch (error) {
      console.log('Using default templates');
    }
  };

  const fetchMyBiodatas = async () => {
    try {
      const res = await api.get('/biodata/my-biodatas');
      setMyBiodatas(res.data.biodatas || []);
    } catch (error) {
      console.error('Failed to fetch biodatas:', error);
    }
  };

  const fetchLimits = async () => {
    try {
      const res = await api.get('/biodata/my-limits');
      setLimitsInfo(res.data);
    } catch (error) {
      console.error('Failed to fetch limits:', error);
    }
  };

  const handlePhotoUpload = async (e, index = null) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a valid image (JPG, PNG, GIF, WEBP)');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setUploadingPhoto(true);
    try {
      const uploadData = new FormData();
      uploadData.append('file', file);
      const res = await api.post('/upload/image', uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (res.data?.url) {
        if (index !== null) {
          // Replace existing photo
          const newPhotos = [...formData.profile_photos];
          newPhotos[index] = { ...newPhotos[index], url: res.data.url };
          setFormData(prev => ({ ...prev, profile_photos: newPhotos }));
        } else {
          // Add new photo
          setFormData(prev => ({
            ...prev,
            profile_photos: [...prev.profile_photos, { url: res.data.url, caption: '', order: prev.profile_photos.length }]
          }));
        }
        toast.success('Photo uploaded!');
      }
    } catch (error) {
      toast.error('Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
      setPhotoUploadIndex(null);
    }
  };

  const removePhoto = (index) => {
    setFormData(prev => ({
      ...prev,
      profile_photos: prev.profile_photos.filter((_, i) => i !== index)
    }));
  };

  const addSocialLink = () => {
    if (!newSocialLink.url.trim()) {
      toast.error('Please enter a URL');
      return;
    }
    let url = newSocialLink.url.trim();
    if (!url.match(/^https?:\/\//i)) url = 'https://' + url;
    
    setFormData(prev => ({
      ...prev,
      social_links: [...prev.social_links, { ...newSocialLink, url }]
    }));
    setNewSocialLink({ platform: 'whatsapp', url: '', label: '' });
    toast.success('Link added!');
  };

  const removeSocialLink = (index) => {
    setFormData(prev => ({
      ...prev,
      social_links: prev.social_links.filter((_, i) => i !== index)
    }));
  };

  const addCustomField = () => {
    if (!newCustomField.label.trim() || !newCustomField.value.trim()) {
      toast.error('Please fill both label and value');
      return;
    }
    setFormData(prev => ({
      ...prev,
      custom_fields: [...prev.custom_fields, { ...newCustomField }]
    }));
    setNewCustomField({ label: '', value: '' });
  };

  const removeCustomField = (index) => {
    setFormData(prev => ({
      ...prev,
      custom_fields: prev.custom_fields.filter((_, i) => i !== index)
    }));
  };

  const handleSave = async (publish = false) => {
    if (!formData.personal_info.name.trim()) {
      toast.error('Name is required');
      return;
    }

    // Check limits when creating new (not editing)
    if (!editingId && !limitsInfo.can_create) {
      toast.error(`Biodata limit reached (${limitsInfo.current_count}/${limitsInfo.max_biodatas}). Upgrade to create more.`);
      return;
    }

    setSaving(true);
    try {
      const dataToSave = { ...formData, is_published: publish };
      
      if (editingId) {
        await api.put(`/biodata/update/${editingId}`, dataToSave);
        toast.success('Biodata updated!');
      } else {
        const res = await api.post('/biodata/create', dataToSave);
        setEditingId(res.data.biodata?.id);
        toast.success('Biodata created!');
      }
      fetchMyBiodatas();
      fetchLimits(); // Refresh limits after save
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async (format = 'png') => {
    if (!bioCardRef.current) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(bioCardRef.current, {
        scale: 3,
        useCORS: true,
        backgroundColor: getTemplateColors().background
      });
      
      if (format === 'pdf') {
        // Dynamic import jspdf for PDF generation
        const { default: jsPDF } = await import('jspdf');
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF({
          orientation: 'portrait',
          unit: 'mm',
          format: 'a4'
        });
        
        // Calculate dimensions to fit A4
        const imgWidth = 210; // A4 width in mm
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        
        pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
        pdf.save(`biodata-${formData.personal_info.name || 'untitled'}.pdf`);
        toast.success('PDF Downloaded!');
      } else {
        const link = document.createElement('a');
        link.download = `biodata-${formData.personal_info.name || 'untitled'}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
        toast.success('Image Downloaded!');
      }
      
      // Track download
      if (editingId) {
        await api.post(`/biodata/track-download/${editingId}`).catch(() => {});
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download');
    } finally {
      setDownloading(false);
    }
  };

  const loadBiodata = async (biodata) => {
    setFormData({
      template: biodata.template || 'gold-royal',
      font_family: biodata.font_family || 'default',
      religious_text: biodata.religious_text || '',
      title: biodata.title || 'BIODATA',
      personal_info: biodata.personal_info || {},
      family_info: biodata.family_info || {},
      contact_info: biodata.contact_info || {},
      profile_photos: biodata.profile_photos || [],
      social_links: biodata.social_links || [],
      custom_fields: biodata.custom_fields || [],
      qr_enabled: biodata.qr_enabled !== false,
      qr_content: biodata.qr_content || '',
      is_published: biodata.is_published || false
    });
    setEditingId(biodata.id);
    setActiveTab('create');
    toast.success('Biodata loaded for editing');
  };

  const deleteBiodata = async (id) => {
    if (!confirm('Delete this biodata?')) return;
    try {
      await api.delete(`/biodata/delete/${id}`);
      toast.success('Biodata deleted');
      fetchMyBiodatas();
      fetchLimits(); // Refresh limits after delete
      if (editingId === id) {
        resetForm();
      }
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const resetForm = () => {
    setFormData({
      template: 'gold-royal',
      religious_text: '!! SHRI GANESHAY NAMAH !!\n!! JAI JHULELA !!',
      title: 'BIODATA',
      personal_info: { name: '', date_of_birth: '', time_of_birth: '', place_of_birth: '', height: '', occupation: '', education: '' },
      family_info: { father_name: '', father_occupation: '', mother_name: '', brother: '', sister: '', caste_community: '' },
      contact_info: { mobile_number: '', secondary_number: '', email: '', residential_address: '', city: '', state: '' },
      profile_photos: [],
      social_links: [],
      custom_fields: [],
      qr_enabled: true,
      qr_content: '',
      is_published: false
    });
    setEditingId(null);
  };

  const getTemplateColors = () => {
    const template = templates.find(t => t.id === formData.template);
    return template?.colors || DEFAULT_TEMPLATES[0].colors;
  };

  const copyShareLink = async () => {
    if (!editingId) {
      toast.error('Save the biodata first');
      return;
    }
    const url = `${window.location.origin}/biodata/${editingId}`;
    const success = await copyToClipboard(url);
    if (success) {
      setCopied(true);
      toast.success('Link copied!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const getSocialIcon = (platform) => {
    const p = SOCIAL_PLATFORMS.find(s => s.id === platform);
    return p?.icon || FaGlobe;
  };

  const getSocialColor = (platform) => {
    const p = SOCIAL_PLATFORMS.find(s => s.id === platform);
    return p?.color || '#6366F1';
  };

  const colors = getTemplateColors();

  // Get tier display info
  const getTierInfo = () => {
    const tiers = {
      free: { name: 'Free', color: 'gray', upgrade: 'Activate your account' },
      activated: { name: 'Activated', color: 'blue', upgrade: 'Upgrade to Premium' },
      premium: { name: 'Premium', color: 'purple', upgrade: null },
      admin: { name: 'Admin', color: 'yellow', upgrade: null }
    };
    return tiers[limitsInfo.tier] || tiers.free;
  };
  const tierInfo = getTierInfo();

  return (
    <div className="min-h-screen pt-24 pb-16 px-4 sm:px-6" data-testid="biodata-builder-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-3">
              <FileText className="w-8 h-8 text-yellow-500" />
              Bio Data Builder
            </h1>
            <p className="text-muted-foreground mt-1">Create beautiful traditional biodatas</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setActiveTab('create')}
              className={`px-4 py-2 rounded-lg font-medium ${activeTab === 'create' ? 'bg-yellow-500 text-black' : 'bg-white/10'}`}
            >
              <Plus className="w-4 h-4 inline mr-1" />
              Create
            </button>
            <button
              onClick={() => setActiveTab('list')}
              className={`px-4 py-2 rounded-lg font-medium ${activeTab === 'list' ? 'bg-yellow-500 text-black' : 'bg-white/10'}`}
            >
              My Biodatas ({myBiodatas.length})
            </button>
          </div>
        </div>

        {/* Limits Banner */}
        <div className={`glass-card p-4 mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 ${
          !limitsInfo.can_create ? 'bg-red-500/10 border-red-500/30' : 'bg-white/5'
        }`}>
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${
              tierInfo.color === 'yellow' ? 'bg-yellow-500/20' :
              tierInfo.color === 'purple' ? 'bg-purple-500/20' :
              tierInfo.color === 'blue' ? 'bg-blue-500/20' : 'bg-gray-500/20'
            }`}>
              <FileText className={`w-5 h-5 ${
                tierInfo.color === 'yellow' ? 'text-yellow-400' :
                tierInfo.color === 'purple' ? 'text-purple-400' :
                tierInfo.color === 'blue' ? 'text-blue-400' : 'text-gray-400'
              }`} />
            </div>
            <div>
              <p className="font-medium">
                <span className={`px-2 py-0.5 text-xs rounded-full mr-2 ${
                  tierInfo.color === 'yellow' ? 'bg-yellow-500/20 text-yellow-400' :
                  tierInfo.color === 'purple' ? 'bg-purple-500/20 text-purple-400' :
                  tierInfo.color === 'blue' ? 'bg-blue-500/20 text-blue-400' : 'bg-gray-500/20 text-gray-400'
                }`}>{tierInfo.name}</span>
                {limitsInfo.current_count} / {limitsInfo.max_biodatas} Biodatas Used
              </p>
              <p className="text-sm text-muted-foreground">
                {limitsInfo.can_create 
                  ? `You can create ${limitsInfo.remaining} more biodata${limitsInfo.remaining !== 1 ? 's' : ''}`
                  : 'Limit reached. ' + (tierInfo.upgrade || 'Contact admin for more.')
                }
              </p>
            </div>
          </div>
          {!limitsInfo.can_create && tierInfo.upgrade && (
            <button className="px-4 py-2 bg-yellow-500 text-black rounded-lg font-medium hover:bg-yellow-400 text-sm whitespace-nowrap">
              {tierInfo.upgrade}
            </button>
          )}
        </div>

        {/* My Biodatas List */}
        {activeTab === 'list' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {myBiodatas.map(bio => (
              <div key={bio.id} className="glass-card p-4 hover:ring-2 hover:ring-yellow-500/50 transition-all">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-bold text-lg">{bio.personal_info?.name || 'Untitled'}</h3>
                    <p className="text-sm text-muted-foreground">{bio.template}</p>
                  </div>
                  <span className={`px-2 py-1 text-xs rounded-full ${bio.is_published ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>
                    {bio.is_published ? 'Published' : 'Draft'}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3">
                  <Eye className="w-3 h-3" /> {bio.view_count || 0} views
                  <Download className="w-3 h-3 ml-2" /> {bio.download_count || 0} downloads
                </div>
                <div className="flex gap-2">
                  <button onClick={() => loadBiodata(bio)} className="flex-1 px-3 py-2 bg-white/10 rounded-lg text-sm hover:bg-white/20 flex items-center justify-center gap-1">
                    <Edit3 className="w-4 h-4" /> Edit
                  </button>
                  {bio.is_published && (
                    <a href={`/biodata/${bio.id}`} target="_blank" rel="noreferrer" className="px-3 py-2 bg-blue-500/20 text-blue-400 rounded-lg text-sm hover:bg-blue-500/30">
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )}
                  <button onClick={() => deleteBiodata(bio.id)} className="px-3 py-2 bg-red-500/20 text-red-400 rounded-lg text-sm hover:bg-red-500/30">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
            {myBiodatas.length === 0 && (
              <div className="col-span-full text-center py-12">
                <FileText className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
                <p className="text-muted-foreground">No biodatas yet</p>
                <button onClick={() => setActiveTab('create')} className="mt-4 px-4 py-2 bg-yellow-500 text-black rounded-lg">
                  Create Your First Biodata
                </button>
              </div>
            )}
          </div>
        )}

        {/* Create/Edit Tab */}
        {activeTab === 'create' && (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            {/* Editor Panel */}
            <div className="space-y-4">
              {/* Template Selection */}
              <div className="glass-card p-4">
                <h3 className="font-bold mb-3 flex items-center gap-2">
                  <Palette className="w-5 h-5 text-yellow-500" />
                  Select Template
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {templates.map(t => (
                    <button
                      key={t.id}
                      onClick={() => setFormData(prev => ({ ...prev, template: t.id }))}
                      className={`p-3 rounded-xl border-2 text-center transition-all ${
                        formData.template === t.id ? 'border-yellow-500 bg-yellow-500/10' : 'border-white/10 hover:border-white/30'
                      }`}
                    >
                      <div className="w-8 h-8 rounded-full mx-auto mb-2" style={{ background: `linear-gradient(135deg, ${t.colors.primary}, ${t.colors.background})` }} />
                      <span className="text-xs font-medium">{t.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Font Selection */}
              <div className="glass-card p-4">
                <h3 className="font-bold mb-3 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-yellow-500" />
                  Select Font Style
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                  {FONT_OPTIONS.map(font => (
                    <button
                      key={font.id}
                      onClick={() => setFormData(prev => ({ ...prev, font_family: font.id }))}
                      className={`p-3 rounded-xl border-2 text-left transition-all ${
                        formData.font_family === font.id ? 'border-yellow-500 bg-yellow-500/10' : 'border-white/10 hover:border-white/30'
                      }`}
                    >
                      <span className="text-sm font-medium" style={{ fontFamily: font.family }}>{font.name}</span>
                      <p className="text-xs text-muted-foreground mt-1" style={{ fontFamily: font.family }}>Preview: अभिषेक ABC 123</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Section Tabs */}
              <div className="flex gap-2 overflow-x-auto pb-2">
                {[
                  { id: 'personal', label: 'Personal', icon: User },
                  { id: 'horoscope', label: 'Horoscope', icon: Star },
                  { id: 'family', label: 'Family', icon: Users },
                  { id: 'expectations', label: 'Expectations', icon: Heart },
                  { id: 'contact', label: 'Contact', icon: Phone },
                  { id: 'photos', label: 'Photos', icon: Image },
                  { id: 'social', label: 'Social', icon: Share2 },
                  { id: 'custom', label: 'Custom', icon: Edit3 }
                ].map(section => (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    className={`px-4 py-2 rounded-lg flex items-center gap-2 whitespace-nowrap ${
                      activeSection === section.id ? 'bg-yellow-500 text-black' : 'bg-white/5 hover:bg-white/10'
                    }`}
                  >
                    <section.icon className="w-4 h-4" />
                    {section.label}
                  </button>
                ))}
              </div>

              {/* Section Content */}
              <div className="glass-card p-4 max-h-[60vh] overflow-y-auto">
                {/* Header Settings */}
                <div className="mb-4 p-3 bg-white/5 rounded-lg">
                  <label className="block text-sm mb-1">Religious Text</label>
                  <textarea
                    value={formData.religious_text}
                    onChange={(e) => setFormData(prev => ({ ...prev, religious_text: e.target.value }))}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm"
                    rows={2}
                    placeholder="!! SHRI GANESHAY NAMAH !!"
                  />
                </div>

                {/* Personal Info */}
                {activeSection === 'personal' && (
                  <div className="space-y-3">
                    <h4 className="font-bold text-yellow-500">Personal Information</h4>
                    {[
                      { key: 'name', label: 'Full Name *', placeholder: 'Enter full name' },
                      { key: 'date_of_birth', label: 'Date of Birth', placeholder: '14th January, 1995' },
                      { key: 'time_of_birth', label: 'Time of Birth', placeholder: '01:05 PM' },
                      { key: 'place_of_birth', label: 'Place of Birth', placeholder: 'Bhopal (M.P.)' },
                      { key: 'height', label: 'Height', placeholder: "5'10\"" },
                      { key: 'weight', label: 'Weight', placeholder: '65 kg' },
                      { key: 'occupation', label: 'Occupation', placeholder: 'Business / Job details' },
                      { key: 'education', label: 'Education', placeholder: 'Graduated / MBA / etc.' },
                      { key: 'income', label: 'Annual Income', placeholder: '₹5-10 LPA' },
                      { key: 'company', label: 'Company/Business', placeholder: 'Company name' }
                    ].map(field => (
                      <div key={field.key}>
                        <label className="block text-sm mb-1">{field.label}</label>
                        <input
                          type="text"
                          value={formData.personal_info[field.key] || ''}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            personal_info: { ...prev.personal_info, [field.key]: e.target.value }
                          }))}
                          className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg"
                          placeholder={field.placeholder}
                        />
                      </div>
                    ))}
                    
                    {/* Dropdown fields */}
                    <div>
                      <label className="block text-sm mb-1">Blood Group</label>
                      <select
                        value={formData.personal_info.blood_group || ''}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          personal_info: { ...prev.personal_info, blood_group: e.target.value }
                        }))}
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg"
                      >
                        <option value="">Select Blood Group</option>
                        {BLOOD_GROUPS.map(bg => <option key={bg} value={bg}>{bg}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm mb-1">Complexion</label>
                      <select
                        value={formData.personal_info.complexion || ''}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          personal_info: { ...prev.personal_info, complexion: e.target.value }
                        }))}
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg"
                      >
                        <option value="">Select Complexion</option>
                        {COMPLEXION_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm mb-1">Body Type</label>
                      <select
                        value={formData.personal_info.body_type || ''}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          personal_info: { ...prev.personal_info, body_type: e.target.value }
                        }))}
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg"
                      >
                        <option value="">Select Body Type</option>
                        {BODY_TYPE_OPTIONS.map(bt => <option key={bt} value={bt}>{bt}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm mb-1">Marital Status</label>
                      <select
                        value={formData.personal_info.marital_status || 'Never Married'}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          personal_info: { ...prev.personal_info, marital_status: e.target.value }
                        }))}
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg"
                      >
                        {MARITAL_STATUS_OPTIONS.map(ms => <option key={ms} value={ms}>{ms}</option>)}
                      </select>
                    </div>
                  </div>
                )}

                {/* Horoscope Info - NEW */}
                {activeSection === 'horoscope' && (
                  <div className="space-y-3">
                    <h4 className="font-bold text-yellow-500">Horoscope Details</h4>
                    <p className="text-sm text-white/60 mb-4">Add astrological details for matching</p>
                    <div>
                      <label className="block text-sm mb-1">Rashi (Zodiac Sign)</label>
                      <select
                        value={formData.horoscope_info?.rashi || ''}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          horoscope_info: { ...prev.horoscope_info, rashi: e.target.value }
                        }))}
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg"
                      >
                        <option value="">Select Rashi</option>
                        {RASHI_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm mb-1">Nakshatra</label>
                      <select
                        value={formData.horoscope_info?.nakshatra || ''}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          horoscope_info: { ...prev.horoscope_info, nakshatra: e.target.value }
                        }))}
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg"
                      >
                        <option value="">Select Nakshatra</option>
                        {NAKSHATRA_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
                      </select>
                    </div>
                    {[
                      { key: 'gotra', label: 'Gotra', placeholder: 'Family Gotra' },
                      { key: 'manglik', label: 'Manglik Status', placeholder: 'Manglik / Non-Manglik / Partial' },
                      { key: 'gan', label: 'Gan', placeholder: 'Dev / Manushya / Rakshas' },
                      { key: 'nadi', label: 'Nadi', placeholder: 'Aadi / Madhya / Antya' }
                    ].map(field => (
                      <div key={field.key}>
                        <label className="block text-sm mb-1">{field.label}</label>
                        <input
                          type="text"
                          value={formData.horoscope_info?.[field.key] || ''}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            horoscope_info: { ...prev.horoscope_info, [field.key]: e.target.value }
                          }))}
                          className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg"
                          placeholder={field.placeholder}
                        />
                      </div>
                    ))}
                  </div>
                )}

                {/* Family Info */}
                {activeSection === 'family' && (
                  <div className="space-y-3">
                    <h4 className="font-bold text-yellow-500">Family Information</h4>
                    {[
                      { key: 'father_name', label: 'Father\'s Name', placeholder: 'Mr. Name' },
                      { key: 'father_occupation', label: 'Father\'s Occupation', placeholder: 'Business / Retired' },
                      { key: 'mother_name', label: 'Mother\'s Name', placeholder: 'Mrs. Name' },
                      { key: 'mother_occupation', label: 'Mother\'s Occupation', placeholder: 'Homemaker' },
                      { key: 'grandfather_name', label: 'Grandfather\'s Name', placeholder: 'Late Mr. Name' },
                      { key: 'native_place', label: 'Native Place', placeholder: 'Original hometown' },
                      { key: 'brother', label: 'Brother(s)', placeholder: 'One Elder (Married) / None' },
                      { key: 'sister', label: 'Sister(s)', placeholder: 'One Younger / None' },
                      { key: 'family_type', label: 'Family Type', placeholder: 'Joint / Nuclear' },
                      { key: 'family_status', label: 'Family Status', placeholder: 'Middle Class / Upper Middle Class' },
                      { key: 'caste_community', label: 'Caste/Community', placeholder: 'Community details' }
                    ].map(field => (
                      <div key={field.key}>
                        <label className="block text-sm mb-1">{field.label}</label>
                        <input
                          type="text"
                          value={formData.family_info?.[field.key] || ''}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            family_info: { ...prev.family_info, [field.key]: e.target.value }
                          }))}
                          className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg"
                          placeholder={field.placeholder}
                        />
                      </div>
                    ))}
                  </div>
                )}

                {/* Expectations Info - NEW */}
                {activeSection === 'expectations' && (
                  <div className="space-y-3">
                    <h4 className="font-bold text-yellow-500">Partner Expectations</h4>
                    <p className="text-sm text-white/60 mb-4">Describe your ideal life partner</p>
                    {[
                      { key: 'age_range', label: 'Preferred Age Range', placeholder: '25-30 years' },
                      { key: 'height_range', label: 'Preferred Height', placeholder: "5'4\" - 5'8\"" },
                      { key: 'education', label: 'Education Preference', placeholder: 'Graduate or above' },
                      { key: 'occupation', label: 'Occupation Preference', placeholder: 'Working professional / Business' },
                      { key: 'location', label: 'Location Preference', placeholder: 'Same city / Any' }
                    ].map(field => (
                      <div key={field.key}>
                        <label className="block text-sm mb-1">{field.label}</label>
                        <input
                          type="text"
                          value={formData.expectations?.[field.key] || ''}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            expectations: { ...prev.expectations, [field.key]: e.target.value }
                          }))}
                          className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg"
                          placeholder={field.placeholder}
                        />
                      </div>
                    ))}
                    <div>
                      <label className="block text-sm mb-1">Other Preferences</label>
                      <textarea
                        value={formData.expectations?.other_preferences || ''}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          expectations: { ...prev.expectations, other_preferences: e.target.value }
                        }))}
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg h-24"
                        placeholder="Describe any other preferences for your life partner..."
                      />
                    </div>
                  </div>
                )}

                {/* Contact Info */}
                {activeSection === 'contact' && (
                  <div className="space-y-3">
                    <h4 className="font-bold text-yellow-500">Contact Information</h4>
                    {[
                      { key: 'mobile_number', label: 'Mobile Number', placeholder: '+91 98765 43210', icon: Phone },
                      { key: 'secondary_number', label: 'Secondary Number', placeholder: '+91 79994 46829', icon: Phone },
                      { key: 'email', label: 'Email', placeholder: 'email@example.com', icon: Mail },
                      { key: 'residential_address', label: 'Address', placeholder: 'House No., Street, Area', icon: MapPin },
                      { key: 'city', label: 'City', placeholder: 'Bhopal' },
                      { key: 'state', label: 'State', placeholder: 'Madhya Pradesh' },
                      { key: 'pincode', label: 'Pincode', placeholder: '462001' }
                    ].map(field => (
                      <div key={field.key}>
                        <label className="block text-sm mb-1 flex items-center gap-2">
                          {field.icon && <field.icon className="w-4 h-4 text-muted-foreground" />}
                          {field.label}
                        </label>
                        <input
                          type="text"
                          value={formData.contact_info[field.key] || ''}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            contact_info: { ...prev.contact_info, [field.key]: e.target.value }
                          }))}
                          className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg"
                          placeholder={field.placeholder}
                        />
                      </div>
                    ))}
                  </div>
                )}

                {/* Photos */}
                {activeSection === 'photos' && (
                  <div className="space-y-3">
                    <h4 className="font-bold text-yellow-500">Profile Photos</h4>
                    <p className="text-sm text-muted-foreground">Add up to 3 photos for your biodata</p>
                    
                    <div className="grid grid-cols-3 gap-3">
                      {formData.profile_photos.map((photo, idx) => (
                        <div key={idx} className="relative group">
                          <div className="aspect-square rounded-lg overflow-hidden border-2 border-yellow-500/30">
                            <img src={getImageUrl(photo.url)} alt="" className="w-full h-full object-cover" />
                          </div>
                          <button
                            onClick={() => removePhoto(idx)}
                            className="absolute top-1 right-1 p-1 bg-red-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-3 h-3" />
                          </button>
                          <input
                            type="text"
                            value={photo.caption || ''}
                            onChange={(e) => {
                              const newPhotos = [...formData.profile_photos];
                              newPhotos[idx] = { ...newPhotos[idx], caption: e.target.value };
                              setFormData(prev => ({ ...prev, profile_photos: newPhotos }));
                            }}
                            className="w-full mt-1 px-2 py-1 text-xs bg-white/5 border border-white/10 rounded"
                            placeholder="Caption"
                          />
                        </div>
                      ))}
                      
                      {formData.profile_photos.length < 3 && (
                        <div
                          onClick={() => fileInputRef.current?.click()}
                          className="aspect-square rounded-lg border-2 border-dashed border-white/20 flex flex-col items-center justify-center cursor-pointer hover:border-yellow-500/50 transition-all"
                        >
                          {uploadingPhoto ? (
                            <div className="animate-spin w-6 h-6 border-2 border-yellow-500 border-t-transparent rounded-full" />
                          ) : (
                            <>
                              <Camera className="w-8 h-8 text-muted-foreground mb-2" />
                              <span className="text-xs text-muted-foreground">Add Photo</span>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                    
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={(e) => handlePhotoUpload(e)}
                      className="hidden"
                    />
                    
                    {/* Image URL Input */}
                    {formData.profile_photos.length < 3 && (
                      <div className="mt-3 flex gap-2">
                        <input
                          type="text"
                          placeholder="Or paste image URL here..."
                          className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && e.target.value.trim()) {
                              const url = e.target.value.trim();
                              if (url.startsWith('http')) {
                                setFormData(prev => ({
                                  ...prev,
                                  profile_photos: [...prev.profile_photos, { url, caption: '', order: prev.profile_photos.length }]
                                }));
                                e.target.value = '';
                                toast.success('Photo added from URL!');
                              } else {
                                toast.error('Please enter a valid URL starting with http');
                              }
                            }
                          }}
                        />
                        <button
                          type="button"
                          onClick={(e) => {
                            const input = e.target.previousElementSibling;
                            const url = input.value.trim();
                            if (url.startsWith('http')) {
                              setFormData(prev => ({
                                ...prev,
                                profile_photos: [...prev.profile_photos, { url, caption: '', order: prev.profile_photos.length }]
                              }));
                              input.value = '';
                              toast.success('Photo added from URL!');
                            } else {
                              toast.error('Please enter a valid URL');
                            }
                          }}
                          className="px-3 py-2 bg-yellow-500 text-black rounded-lg text-sm font-medium hover:bg-yellow-400"
                        >
                          Add URL
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Social Links */}
                {activeSection === 'social' && (
                  <div className="space-y-3">
                    <h4 className="font-bold text-yellow-500">Social Media Links</h4>
                    
                    {/* Existing Links */}
                    <div className="space-y-2">
                      {formData.social_links.map((link, idx) => {
                        const Icon = getSocialIcon(link.platform);
                        return (
                          <div key={idx} className="flex items-center gap-2 p-2 bg-white/5 rounded-lg">
                            <Icon className="w-5 h-5" style={{ color: getSocialColor(link.platform) }} />
                            <span className="flex-1 text-sm truncate">{link.label || link.platform}</span>
                            <button onClick={() => removeSocialLink(idx)} className="p-1 hover:bg-red-500/20 rounded">
                              <X className="w-4 h-4 text-red-400" />
                            </button>
                          </div>
                        );
                      })}
                    </div>

                    {/* Add New Link */}
                    <div className="p-3 bg-white/5 rounded-lg space-y-2">
                      <div className="grid grid-cols-3 gap-2">
                        {SOCIAL_PLATFORMS.map(p => {
                          const Icon = p.icon;
                          return (
                            <button
                              key={p.id}
                              onClick={() => setNewSocialLink(prev => ({ ...prev, platform: p.id }))}
                              className={`p-2 rounded-lg flex flex-col items-center gap-1 transition-all ${
                                newSocialLink.platform === p.id ? 'ring-2 ring-yellow-500' : 'bg-white/5'
                              }`}
                              style={{ backgroundColor: newSocialLink.platform === p.id ? p.bgColor : undefined }}
                            >
                              <Icon className="w-5 h-5" style={{ color: p.color }} />
                              <span className="text-[10px]">{p.name}</span>
                            </button>
                          );
                        })}
                      </div>
                      <input
                        type="text"
                        value={newSocialLink.label}
                        onChange={(e) => setNewSocialLink(prev => ({ ...prev, label: e.target.value }))}
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm"
                        placeholder="Label (e.g. Your Channel)"
                      />
                      <input
                        type="text"
                        value={newSocialLink.url}
                        onChange={(e) => setNewSocialLink(prev => ({ ...prev, url: e.target.value }))}
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm"
                        placeholder="URL or username"
                      />
                      <button
                        onClick={addSocialLink}
                        className="w-full px-4 py-2 bg-yellow-500/20 text-yellow-500 rounded-lg text-sm hover:bg-yellow-500/30 flex items-center justify-center gap-2"
                      >
                        <Plus className="w-4 h-4" /> Add Link
                      </button>
                    </div>

                    {/* QR Code Settings */}
                    <div className="p-3 bg-white/5 rounded-lg space-y-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.qr_enabled}
                          onChange={(e) => setFormData(prev => ({ ...prev, qr_enabled: e.target.checked }))}
                          className="w-4 h-4"
                        />
                        <QrCode className="w-4 h-4 text-yellow-500" />
                        <span>Enable QR Code</span>
                      </label>
                      {formData.qr_enabled && (
                        <input
                          type="text"
                          value={formData.qr_content}
                          onChange={(e) => setFormData(prev => ({ ...prev, qr_content: e.target.value }))}
                          className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm"
                          placeholder="QR content (URL, Phone, etc.)"
                        />
                      )}
                    </div>
                  </div>
                )}

                {/* Custom Fields */}
                {activeSection === 'custom' && (
                  <div className="space-y-3">
                    <h4 className="font-bold text-yellow-500">Custom Fields</h4>
                    <p className="text-sm text-muted-foreground">Add any additional information</p>
                    
                    <div className="space-y-2">
                      {formData.custom_fields.map((field, idx) => (
                        <div key={idx} className="flex items-center gap-2 p-2 bg-white/5 rounded-lg">
                          <span className="font-medium text-sm">{field.label}:</span>
                          <span className="flex-1 text-sm">{field.value}</span>
                          <button onClick={() => removeCustomField(idx)} className="p-1 hover:bg-red-500/20 rounded">
                            <X className="w-4 h-4 text-red-400" />
                          </button>
                        </div>
                      ))}
                    </div>

                    <div className="p-3 bg-white/5 rounded-lg space-y-2">
                      <input
                        type="text"
                        value={newCustomField.label}
                        onChange={(e) => setNewCustomField(prev => ({ ...prev, label: e.target.value }))}
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm"
                        placeholder="Field Label (e.g. Horoscope)"
                      />
                      <input
                        type="text"
                        value={newCustomField.value}
                        onChange={(e) => setNewCustomField(prev => ({ ...prev, value: e.target.value }))}
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm"
                        placeholder="Value"
                      />
                      <button
                        onClick={addCustomField}
                        className="w-full px-4 py-2 bg-white/10 rounded-lg text-sm hover:bg-white/20 flex items-center justify-center gap-2"
                      >
                        <Plus className="w-4 h-4" /> Add Field
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => handleSave(false)}
                  disabled={saving}
                  className="flex-1 min-w-[120px] px-4 py-3 bg-white/10 rounded-xl font-medium hover:bg-white/20 flex items-center justify-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  {saving ? 'Saving...' : 'Save Draft'}
                </button>
                <button
                  onClick={() => handleSave(true)}
                  disabled={saving}
                  className="flex-1 min-w-[120px] px-4 py-3 bg-green-500 text-white rounded-xl font-medium hover:bg-green-600 flex items-center justify-center gap-2"
                >
                  <Eye className="w-4 h-4" />
                  Save & Publish
                </button>
                <button
                  onClick={() => handleDownload('png')}
                  disabled={downloading}
                  className="px-4 py-3 bg-yellow-500 text-black rounded-xl font-medium hover:bg-yellow-400 flex items-center justify-center gap-2"
                  title="Download as PNG Image"
                >
                  <Image className="w-4 h-4" />
                  PNG
                </button>
                <button
                  onClick={() => handleDownload('pdf')}
                  disabled={downloading}
                  className="px-4 py-3 bg-red-500 text-white rounded-xl font-medium hover:bg-red-400 flex items-center justify-center gap-2"
                  title="Download as PDF"
                >
                  <FileText className="w-4 h-4" />
                  PDF
                </button>
                {editingId && (
                  <>
                    <button
                      onClick={copyShareLink}
                      className="px-4 py-3 bg-blue-500/20 text-blue-400 rounded-xl font-medium hover:bg-blue-500/30 flex items-center justify-center gap-2"
                    >
                      {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => setShowQR(true)}
                      className="px-4 py-3 bg-purple-500/20 text-purple-400 rounded-xl font-medium hover:bg-purple-500/30"
                    >
                      <QrCode className="w-4 h-4" />
                    </button>
                  </>
                )}
                <button
                  onClick={resetForm}
                  className="px-4 py-3 bg-red-500/20 text-red-400 rounded-xl font-medium hover:bg-red-500/30"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Preview Panel */}
            <div className="xl:sticky xl:top-24">
              <h3 className="font-bold mb-3 flex items-center gap-2">
                <Eye className="w-5 h-5 text-yellow-500" />
                Live Preview
              </h3>
              
              {/* Biodata Card Preview */}
              <div 
                ref={bioCardRef}
                className="rounded-2xl overflow-hidden shadow-2xl"
                style={{ 
                  backgroundColor: colors.background,
                  color: colors.text,
                  maxWidth: '500px',
                  fontFamily: FONT_OPTIONS.find(f => f.id === formData.font_family)?.family || "'Poppins', sans-serif"
                }}
              >
                {/* Ornate Border Top */}
                <div className="h-2" style={{ background: `linear-gradient(90deg, ${colors.primary}, ${colors.accent}, ${colors.primary})` }} />
                
                {/* Header with Religious Text */}
                <div className="text-center py-4 px-4" style={{ borderBottom: `2px solid ${colors.border}` }}>
                  {/* God Image Placeholder */}
                  <div className="w-16 h-16 mx-auto mb-2 rounded-full flex items-center justify-center text-3xl" style={{ backgroundColor: colors.primary + '20' }}>
                    🙏
                  </div>
                  {formData.religious_text && (
                    <p className="text-sm font-medium whitespace-pre-line" style={{ color: colors.primary }}>
                      {formData.religious_text}
                    </p>
                  )}
                  <h2 className="text-2xl font-bold mt-2" style={{ color: colors.primary }}>
                    {formData.title || 'BIODATA'}
                  </h2>
                </div>

                {/* Main Content */}
                <div className="p-4 flex gap-4">
                  {/* Left: Details */}
                  <div className="flex-1 space-y-2 text-sm">
                    {/* Personal Info */}
                    {Object.entries({
                      'Name': formData.personal_info.name,
                      'Date of Birth': formData.personal_info.date_of_birth,
                      'Time of Birth': formData.personal_info.time_of_birth,
                      'Place of Birth': formData.personal_info.place_of_birth,
                      'Height': formData.personal_info.height,
                      'Occupation': formData.personal_info.occupation,
                      'Education': formData.personal_info.education
                    }).filter(([_, v]) => v).map(([label, value]) => (
                      <div key={label} className="flex">
                        <span className="w-28 font-medium" style={{ color: colors.primary }}>{label}</span>
                        <span className="mr-2">:</span>
                        <span className="flex-1 font-semibold">{value}</span>
                      </div>
                    ))}

                    {/* Family Info */}
                    {Object.entries({
                      'Father': formData.family_info.father_name,
                      "Father's Occupation": formData.family_info.father_occupation,
                      'Mother': formData.family_info.mother_name,
                      'Brother': formData.family_info.brother,
                      'Sister': formData.family_info.sister,
                      'In Sindh': formData.family_info.caste_community
                    }).filter(([_, v]) => v).map(([label, value]) => (
                      <div key={label} className="flex">
                        <span className="w-28 font-medium" style={{ color: colors.primary }}>{label}</span>
                        <span className="mr-2">:</span>
                        <span className="flex-1 font-semibold">{value}</span>
                      </div>
                    ))}

                    {/* Custom Fields */}
                    {formData.custom_fields.map((field, idx) => (
                      <div key={idx} className="flex">
                        <span className="w-28 font-medium" style={{ color: colors.primary }}>{field.label}</span>
                        <span className="mr-2">:</span>
                        <span className="flex-1 font-semibold">{field.value}</span>
                      </div>
                    ))}
                  </div>

                  {/* Right: Photos */}
                  {formData.profile_photos.length > 0 && (
                    <div className="w-32 space-y-2">
                      {formData.profile_photos.map((photo, idx) => (
                        <div key={idx} className="rounded-lg overflow-hidden border-2" style={{ borderColor: colors.primary }}>
                          <img src={getImageUrl(photo.url)} alt="" className="w-full h-32 object-cover" />
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Contact & Address Section */}
                <div className="px-4 pb-4">
                  <div className="p-3 rounded-lg" style={{ backgroundColor: colors.primary + '10', border: `1px solid ${colors.border}` }}>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <p className="font-medium mb-1" style={{ color: colors.primary }}>Residential Address</p>
                        <p>{formData.contact_info.residential_address}</p>
                        <p>{formData.contact_info.city}{formData.contact_info.state ? `, ${formData.contact_info.state}` : ''}</p>
                      </div>
                      <div>
                        <p className="font-medium mb-1" style={{ color: colors.primary }}>Contact No.</p>
                        {formData.contact_info.mobile_number && (
                          <p className="flex items-center gap-1">
                            <FaPhone className="w-3 h-3" style={{ color: colors.primary }} />
                            {formData.contact_info.mobile_number}
                          </p>
                        )}
                        {formData.contact_info.secondary_number && (
                          <p className="flex items-center gap-1">
                            <FaPhone className="w-3 h-3" style={{ color: colors.primary }} />
                            {formData.contact_info.secondary_number}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Social Links Footer */}
                {formData.social_links.length > 0 && (
                  <div className="px-4 pb-4">
                    <div className="flex flex-wrap justify-center gap-3">
                      {formData.social_links.map((link, idx) => {
                        const Icon = getSocialIcon(link.platform);
                        const color = getSocialColor(link.platform);
                        return (
                          <a
                            key={idx}
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 px-3 py-2 rounded-full text-xs font-medium transition-all hover:scale-105"
                            style={{ backgroundColor: color + '20', color: color, border: `1px solid ${color}50` }}
                          >
                            <Icon className="w-4 h-4" />
                            {link.label || link.platform}
                          </a>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* QR Code */}
                {formData.qr_enabled && formData.qr_content && (
                  <div className="px-4 pb-4 flex justify-center">
                    <div className="p-2 bg-white rounded-lg">
                      <QRCodeSVG value={formData.qr_content} size={80} level="M" />
                    </div>
                  </div>
                )}

                {/* Bottom Border */}
                <div className="h-2" style={{ background: `linear-gradient(90deg, ${colors.primary}, ${colors.accent}, ${colors.primary})` }} />
              </div>
            </div>
          </div>
        )}

        {/* QR Modal */}
        {showQR && editingId && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShowQR(false)}>
            <div className="bg-[#1a1a1a] rounded-2xl p-8 text-center" onClick={e => e.stopPropagation()}>
              <h3 className="text-xl font-bold mb-4">Scan to View Biodata</h3>
              <div className="bg-white p-4 rounded-xl inline-block mb-4">
                <QRCodeSVG value={`${window.location.origin}/biodata/${editingId}`} size={200} level="H" />
              </div>
              <p className="text-sm text-muted-foreground mb-4">{`${window.location.origin}/biodata/${editingId}`}</p>
              <button onClick={() => setShowQR(false)} className="px-6 py-2 bg-yellow-500 text-black rounded-lg font-medium">Close</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BioDataBuilder;
